<template>
  <div
    data-component="InsurancePersonalDataStep1Form"
    class="insurance-personal-data-step-1-form"
  >
    <h2 class="gl_form-title small">
      {{ t('hiring.common.insurance-personal-data-step-1.form-legend') }}
    </h2>

    <CardInfoComponent
      v-if="message"
      :icon="message.icon"
    >
      {{ message.label }}
    </CardInfoComponent>

    <BaseInput
      :label="nameLabel"
      id="name"
      v-model="name"
      :placeholder="namePlaceholder"
      :rules="[validationRules.required, validationRules.validName]"
      :maxlength="50"
      :uppercase="true"
      @keydown="isAlphabeticalKey"
    />

    <BaseInput
      :label="surnameLabel"
      id="surname1"
      v-model="surname1"
      :placeholder="surnamePlaceholder"
      :rules="[validationRules.required, validationRules.validSurname]"
      :maxlength="50"
      :uppercase="true"
      @keydown="isAlphabeticalKey"
    />

    <BaseInput
      :label="surname2Label"
      id="surname2"
      v-model="surname2"
      :placeholder="surname2Placeholder"
      :rules="[
        nationality === SPANISH_NATIONALITY ? validationRules.required : () => true,
        validationRules.validSurname
      ]"
      :maxlength="50"
      :uppercase="true"
      @keydown="isAlphabeticalKey"
    />
    <v-text-field
      ref="$date"
      id="complete-data"
      class="full-data"
      v-model="completaData"
      :rules="completeDataValidations"
      :hide-details="true"
    />

    <ErrorMessageComponent :messages="fieldValidationErrorMessage" />

    <FormField
      v-if="needsBirthdate"
      :statement="birthDateLabel"
      class="birthdate-form-field"
    >
      <BirthDatePickerComponent
        :legend="t('rater.common.more-info.birth-date.legend')"
        id="birthDate"
        v-model="birthDate"
        :rules="[validationRules.required, validationRules.tooYoung, validationRules.tooOld]"
      />
    </FormField>

    <RadioButtonGroup
      :legend="sexLabel"
      id="sex"
      :value="gender"
      :options="genders"
      v-model="gender"
      :rules="[validationRules.radioRequired]"
      flexDirection="row"
    />
    <HealthPolicyNumber
      v-if="currentInsured.hasHealthPolicy"
      id="policyNumber"
      v-model="healthPolicyNumber"
      :label="t('copy_policy_number_field_label')"
      :placeholder="t('copy_policy_number_field_placeholder', { n: MAX_CHARS_POLICY_NUMBER })"
      :message="configurations.tags.policyNumberAlert"
      :editable="!samePolicyNumber"
    >
      <BaseCheckBox
        v-if="holder.hasHealthPolicy && currentInsured.role === UserRoleEnum.INSURED"
        id="samePolicyNumber"
        :label="t('hiring.common.insurance-personal-data-step-2.check')"
        v-model="samePolicyNumber"
      />
    </HealthPolicyNumber>
  </div>
</template>

<script lang="ts" setup>
  import { ref, computed, onBeforeMount, watch } from 'vue'
  import { useI18n } from 'vue-i18n'

  import { useRules } from '@/modules/core/composables/useRulesComposable'
  import RadioButtonGroup from '@/modules/core/components/ui/RadioButtonGroupComponent.vue'
  import { MAX_CHARS_POLICY_NUMBER } from '@/modules/core/constants/common'
  import BaseInput from '@/modules/core/components/ui/BaseInput.vue'
  import BaseCheckBox from '@/modules/core/components/ui/BaseCheckBox.vue'
  import HealthPolicyNumber from '@/modules/core/components/formFields/HealthPolicyNumber.vue'
  import type { OptionsValuesInterface } from '@/modules/core/models/types'
  import { UserRoleEnum } from '@/modules/core/models/enums'
  import { useLoadingStore } from '@/modules/core/stores/loadingStore'
  import Insured from '@/modules/common/common/models/APP/Insured'
  import ErrorMessageComponent from '@/modules/core/components/formFields/ErrorMessageComponent.vue'
  import BirthDatePickerComponent from '@/modules/core/components/formFields/BirthDatePickerComponent.vue'
  import FormField from '@/modules/core/components/utils/FormField.vue'
  import CardInfoComponent from '@/modules/common/common/components/panels/CardInfoComponent.vue'
  import {
    hiringAges,
    hiringMaxAge,
    hiringMinAge
  } from '@/modules/core/composables/minMaxAgeComposable'
  import { getBranchMaxAge } from '@/modules/core/utils/BranchUtils'
  import { isAlphabeticalKey } from '@/utils/input.utils'

  const SPANISH_NATIONALITY = 'ESPA'

  const { t } = useI18n()
  const { required, validNameV2, validSurname, tooYoung, tooOld } = useRules()
  const loadingStore = useLoadingStore()
  defineExpose({ triggerSubmit })

  interface PropsInterface {
    nextForm: String[]
    answers: Record<string, any>
    configurations: Record<string, any>
    extra?: Record<string, any>
  }
  const props: PropsInterface = withDefaults(defineProps<PropsInterface>(), {})

  const currentInsured = new Insured(
    props.extra?.customInsuredProp
      ? props.answers[props.extra.customInsuredProp]
      : props.answers.currentInsured
  )
  const currentInsuredUser = currentInsured.userDetails
  const nationality = currentInsured.addressDetails.country
  const holder = props.extra?.usePolicyHolderAsHolder
    ? props.answers.policyHolder
    : props.answers.holder

  const isHolder = computed(() => currentInsured.role === UserRoleEnum.HOLDER_INSURED)

  const currentHiringAge = hiringAges(props.configurations?.hiringMaxAges, currentInsured?.role)

  const minAgeValue = hiringMinAge(props.extra, currentHiringAge, props.configurations)

  const maxAgeValue = hiringMaxAge(props.extra, currentHiringAge, props.configurations)
  const validationRules = {
    required,
    validSurname,
    radioRequired: (value: string) => required(value, 'common.validations.option'),
    validName: (value: string) =>
      validNameV2(value, 'hiring.common.insurance-personal-data-step-1.name-format-not-valid'),
    validNameAndSurname: () => {
      const nameLength = name.value?.length ?? 0
      const surname1Length = surname1.value?.length ?? 0
      const surname2Length = surname2.value?.length ?? 0
      return (
        nameLength + surname1Length + surname2Length <= 58 ||
        t('common.validations.name-surname-error')
      )
    },
    tooYoung: (value: string) => tooYoung(value, minAgeValue, null, 'common.validations.min-age'),
    tooOld: (value: string) =>
      tooOld(
        value,
        maxAgeValue,
        null,
        isHolder.value
          ? `common.validations.max-age-${getBranchMaxAge()}`
          : 'common.validations.date'
      )
  }

  const completaData = computed(() => `${name.value}${surname1.value}${surname2.value}`)
  const completeDataValidations = [validationRules.validNameAndSurname]
  const fieldValidationErrorMessage = computed(() => {
    const nameLength = name.value?.length ?? 0
    const surname1Length = surname1.value?.length ?? 0
    const surname2Length = surname2.value?.length ?? 0
    return nameLength + surname1Length + surname2Length <= 58
      ? ''
      : t('common.validations.name-surname-error')
  })
  const genders = ref<OptionsValuesInterface[]>(props.configurations.genderOptions)
  const birthDate = ref(currentInsuredUser.birthDate)
  const name = ref<string | null>((currentInsuredUser.name ?? '')?.toUpperCase())
  const surname1 = ref<string | null>((currentInsuredUser.surname1 ?? '')?.toUpperCase())
  const surname2 = ref<string | null>((currentInsuredUser.surname2 ?? '')?.toUpperCase())
  const gender = ref<string | null>(currentInsuredUser.gender ?? props.configurations.defaultGender)
  const samePolicyNumber = ref(false)
  const healthPolicyNumber = ref<string | null>(currentInsured.healthPolicyNumber ?? '')

  const message = computed(() => {
    if (props.extra?.customAlertMessage) {
      return props.extra.customAlertMessage
    }
    if (currentInsured.role !== UserRoleEnum.INSURED) {
      return props.configurations.tags.personalDataAlert
    } else {
      return props.configurations.tags.personalDataInsuredAlert
    }
  })

  const nameLabel = computed(() => {
    const isInsured = currentInsured.role === UserRoleEnum.INSURED
    const i18n = isInsured ? 'not-holder' : 'holder'
    return t(`hiring.common.insurance-personal-data-step-1.label-name.${i18n}`)
  })
  const namePlaceholder = computed(() => {
    const isInsured = currentInsured.role === UserRoleEnum.INSURED
    const i18n = isInsured ? 'not-holder' : 'holder'
    return t(`hiring.common.insurance-personal-data-step-1.placeholder-name.${i18n}`)
  })

  const surnameLabel = computed(() => {
    const isInsured = currentInsured.role === UserRoleEnum.INSURED
    const i18n = isInsured ? 'not-holder' : 'holder'
    return t(`hiring.common.insurance-personal-data-step-1.label-surname.${i18n}`)
  })

  const surnamePlaceholder = computed(() => {
    const isInsured = currentInsured.role === UserRoleEnum.INSURED
    const i18n = isInsured ? 'not-holder' : 'holder'
    return t(`hiring.common.insurance-personal-data-step-1.placeholder-surname.${i18n}`)
  })

  const surname2Label = computed(() => {
    const isInsured = currentInsured.role === UserRoleEnum.INSURED
    const i18n = isInsured ? 'not-holder' : 'holder'
    return t(`hiring.common.insurance-personal-data-step-1.label-surname2.${i18n}`)
  })

  const surname2Placeholder = computed(() => {
    const isInsured = currentInsured.role === UserRoleEnum.INSURED
    const i18n = isInsured ? 'not-holder' : 'holder'
    return t(`hiring.common.insurance-personal-data-step-1.placeholder-surname2.${i18n}`)
  })

  const sexLabel = computed(() => {
    const isInsured = currentInsured.role === UserRoleEnum.INSURED
    const i18n = isInsured ? 'not-holder' : 'holder'
    return t(`hiring.common.insurance-personal-data-step-1.label-sex.${i18n}`)
  })

  const birthDateLabel = computed(() => {
    const isInsured = currentInsured.role === UserRoleEnum.INSURED
    const i18n = isInsured ? 'not-me-singular' : 'me'
    return t(`rater.common.more-info.birth-date.title-${i18n}`)
  })

  const needsBirthdate = computed(() => props.extra?.needsBirthdate)

  function triggerSubmit() {
    const page = props.nextForm[0]

    if (needsBirthdate.value) {
      currentInsured.userDetails.birthDate = birthDate.value
    }

    currentInsured.userDetails.name = name.value?.toUpperCase()
    currentInsured.userDetails.surname1 = surname1.value?.toUpperCase()
    currentInsured.userDetails.surname2 = surname2.value?.toUpperCase()
    currentInsured.userDetails.gender = gender.value
    currentInsured.healthPolicyNumber = healthPolicyNumber.value!

    const answers = props.extra?.customInsuredProp
      ? { [props.extra.customInsuredProp]: currentInsured }
      : props.extra?.currentInsuredIndex === 'holder'
      ? { holder: currentInsured }
      : { userDetails: currentInsured.userDetails, healthPolicyNumber: healthPolicyNumber.value }
    return { page, answers }
  }

  function beforeMount() {
    setTimeout(loadingStore.stopLoading, 300)
  }

  onBeforeMount(beforeMount)

  watch(samePolicyNumber, (newValue) => {
    if (newValue) {
      healthPolicyNumber.value = holder.healthPolicyNumber
    }
  })
</script>
<style lang="scss" scoped>
  .insurance-personal-data-step-1-form {
    .full-data {
      // display: none;
      visibility: hidden;
      height: 0;
    }
  }
</style>
